import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Flex,
  Text,
  Button,
  Badge,
  Spinner,
  Alert,
  AlertIcon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  HStack,
  VStack,
  useToast,
  useColorModeValue,
} from "@chakra-ui/react";
import { ArrowBackIcon, DeleteIcon } from "@chakra-ui/icons";
import { FiRefreshCw, FiZap, FiRotateCcw, FiFileText } from "react-icons/fi";
import { releaseService } from "../services/edosService";
import api from "../services/api";

const ENV_COLORS = {
  dev: "blue",
  qa: "orange",
  uat: "purple",
  staging: "yellow",
  production: "green",
};

const DEPLOY_STATUS_COLORS = {
  pending: "gray",
  deploying: "blue",
  deployed: "green",
  failed: "red",
  rolled_back: "orange",
};

const normalizeTicketRows = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.tickets)) return payload.data.tickets;
  if (Array.isArray(payload?.tickets)) return payload.tickets;
  return [];
};

export default function ReleaseDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const bg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const headerBg = useColorModeValue("gray.50", "gray.700");

  const [release, setRelease] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [releaseNotes, setReleaseNotes] = useState("");
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [notesLoading, setNotesLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const releaseRes = await releaseService.getById(id);
      setRelease(releaseRes?.data?.data || releaseRes?.data);

      const allRows = [];
      let page = 1;
      let totalPages = 1;
      do {
        const res = await api.get("/api/tickets", {
          params: {
            page,
            limit: 100,
            releaseId: id,
            _viewMode: "all",
            sortBy: "createdAt",
            sortOrder: "desc",
          },
        });
        const payload = res?.data;
        allRows.push(...normalizeTicketRows(payload));
        totalPages = Number(payload?.totalPages || payload?.data?.totalPages || 1);
        page += 1;
      } while (page <= totalPages && page <= 50);
      setTickets(allRows);
    } catch {
      toast({ title: "Failed to load release details", status: "error" });
      setRelease(null);
      setTickets([]);
    } finally {
      setLoading(false);
    }
  }, [id, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDeploy = async () => {
    if (!window.confirm("Trigger deployment for this release?")) return;
    setActionLoading(true);
    try {
      await releaseService.deploy(id);
      toast({ title: "Deployment triggered", status: "success" });
      load();
    } catch (err) {
      toast({ title: err?.response?.data?.message || "Deploy failed", status: "error" });
    } finally {
      setActionLoading(false);
    }
  };

  const handleRollback = async () => {
    const reason = window.prompt("Rollback reason:");
    if (!reason) return;
    setActionLoading(true);
    try {
      await releaseService.rollback(id, { reason });
      toast({ title: "Rolled back", status: "success" });
      load();
    } catch (err) {
      toast({ title: err?.response?.data?.message || "Rollback failed", status: "error" });
    } finally {
      setActionLoading(false);
    }
  };

  const handleGenerateNotes = async () => {
    setNotesLoading(true);
    try {
      const res = await releaseService.generateNotes(id);
      setReleaseNotes(res?.data?.data?.notes || res?.data?.notes || JSON.stringify(res?.data, null, 2));
    } catch {
      setReleaseNotes("Failed to generate notes.");
    } finally {
      setNotesLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this release?")) return;
    setActionLoading(true);
    try {
      await releaseService.delete(id);
      toast({ title: "Release deleted", status: "success" });
      navigate("/edos/releases");
    } catch (err) {
      toast({ title: err?.response?.data?.message || "Failed to delete release", status: "error" });
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" minH="60vh">
        <Spinner size="xl" color="green.500" />
      </Flex>
    );
  }

  if (!release) {
    return (
      <Box p={6}>
        <Alert status="error"><AlertIcon />Release not found.</Alert>
        <Button mt={4} leftIcon={<ArrowBackIcon />} onClick={() => navigate("/edos/releases")}>Back to Releases</Button>
      </Box>
    );
  }

  return (
    <Box p={6}>
      <Flex justify="space-between" align="center" mb={6} wrap="wrap" gap={3}>
        <HStack>
          <Button leftIcon={<ArrowBackIcon />} variant="ghost" onClick={() => navigate("/edos/releases")}>Releases</Button>
          <Text fontSize="2xl" fontWeight="bold">{release.releaseName}</Text>
          <Badge colorScheme={ENV_COLORS[release.environment] || "gray"}>{release.environment}</Badge>
          <Badge colorScheme={DEPLOY_STATUS_COLORS[release.deploymentStatus] || "gray"}>{release.deploymentStatus}</Badge>
        </HStack>
        <HStack>
          <Button leftIcon={<FiRefreshCw />} variant="outline" onClick={load}>Refresh</Button>
          {release.deploymentStatus !== "deployed" && (
            <Button leftIcon={<FiZap />} colorScheme="green" isLoading={actionLoading} onClick={handleDeploy}>Deploy</Button>
          )}
          {release.deploymentStatus === "deployed" && (
            <Button leftIcon={<FiRotateCcw />} colorScheme="orange" isLoading={actionLoading} onClick={handleRollback}>Rollback</Button>
          )}
          <Button leftIcon={<DeleteIcon />} colorScheme="red" variant="outline" isLoading={actionLoading} onClick={handleDelete}>Delete</Button>
        </HStack>
      </Flex>

      <VStack align="stretch" spacing={6}>
        <Box p={4} bg={bg} border="1px" borderColor={borderColor} borderRadius="md">
          <Text fontSize="sm" color="gray.500">Planned Release Date</Text>
          <Text fontWeight="semibold">{release.plannedReleaseDate ? new Date(release.plannedReleaseDate).toLocaleDateString() : "-"}</Text>
          {release.description && (
            <>
              <Text mt={3} fontSize="sm" color="gray.500">Description</Text>
              <Text>{release.description}</Text>
            </>
          )}
        </Box>

        <Box p={4} bg={bg} border="1px" borderColor={borderColor} borderRadius="md">
          <Flex justify="space-between" align="center" mb={3}>
            <Text fontWeight="bold">Release Notes</Text>
            <Button size="sm" leftIcon={<FiFileText />} isLoading={notesLoading} onClick={handleGenerateNotes}>Generate Notes</Button>
          </Flex>
          <Box as="pre" fontSize="sm" bg={headerBg} p={3} borderRadius="md" whiteSpace="pre-wrap" overflowX="auto">
            {releaseNotes || "No notes generated yet."}
          </Box>
        </Box>

        <Box bg={bg} border="1px" borderColor={borderColor} borderRadius="md" overflow="hidden">
          <Box px={4} py={3} bg={headerBg} borderBottom="1px" borderColor={borderColor}>
            <Text fontWeight="bold">Linked Tickets ({tickets.length})</Text>
          </Box>
          {tickets.length === 0 ? (
            <Alert status="info" m={4}><AlertIcon />No tickets linked to this release.</Alert>
          ) : (
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th>Ticket</Th>
                  <Th>Title</Th>
                  <Th>Status</Th>
                  <Th>Priority</Th>
                </Tr>
              </Thead>
              <Tbody>
                {tickets.map((ticket) => (
                  <Tr key={ticket._id} _hover={{ bg: headerBg, cursor: "pointer" }} onClick={() => navigate(`/Ticketing/${ticket._id}`)}>
                    <Td>{ticket.ticketCode || ticket.ticketId || "-"}</Td>
                    <Td><Text noOfLines={1}>{ticket.title || "-"}</Text></Td>
                    <Td><Badge colorScheme="gray">{String(ticket.workflowState || "open").replace(/_/g, " ")}</Badge></Td>
                    <Td><Badge colorScheme="orange">{ticket.priority || "-"}</Badge></Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </Box>
      </VStack>
    </Box>
  );
}
